// src/features/patient/care-treatment/components/VisitDetailView.tsx

/**
 * Purpose : One visit day (or one call, message or letter): the date, where
 * and with whom, a chip per part with its time, the visit summary exactly as
 * the record wrote it, then one section per part in time order with its
 * notes (short ones in full) and its results & orders, each group under its
 * own heading (icon, count, what it holds); what
 * was assessed and ordered, vitals, other records from the same department
 * within 14 days, and the one Source box. Previous / next visit day follow
 * the timeline's filters. Every block hides itself when it has nothing.
 *
 * Contents :
 *
 * * function(VisitDetailView) : the visit-day page
 * * function(PartSection) : one part (encounter) of the day
 * * function(InlineNote) : a note inside its part (in full when 15 lines or fewer)
 * * function(GroupHeading) : a group's heading (Doctors' notes / Results & orders)
 * * function(OrdersGroup) : a part's results & orders, the first 3 then "Show all N"
 * * function(NoteRow) : one note row, opening the note viewer
 * * function(Fact) : one key fact box
 * * function(mergeSources) : the day's sources, each file once with its whole page range
 */

import { useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { cn } from "@/shared/lib/utils";
import type { CarePlanOut, Encounter, LinkedItem, Note, Source, Vital } from "@/features/patient/care-treatment/api/careTimelineTypes";
import {
  formatDateOnly,
  formatLongDate,
  formatNoteWhen,
  formatTime,
  formatWhen,
  noteTypeLabel,
} from "@/features/patient/care-treatment/utils/careTimeline";
import { recordTypeLabel, visitKind, kindMeta } from "@/features/patient/care-treatment/utils/careNaming";
import { noteLineCount, noteLines, recordText, reflowText } from "@/features/patient/care-treatment/utils/noteText";
import {
  dayClinicians,
  dayHasDetails,
  dayNotes,
  partNotes,
  summaryLines,
  visitDayPlace,
  type VisitDay,
} from "@/features/patient/care-treatment/utils/visitDays";
import { EmptyCardNote, LinkedChips, WhereFrom } from "./careParts";
import { OriginalViewer } from "./OriginalViewer";
import { VisitDiagnoses } from "./VisitDiagnoses";
import { DETAILS_ONLY_TEXT } from "./VisitsTimeline";
import {
  bodyTextClass,
  cardClass,
  dividerClass,
  factBoxClass,
  faintTextClass,
  greyChipClass,
  inferredNoticeClass,
  labelTextClass,
  navyChipClass,
  outlineButtonClass,
  pageClass,
  redIconBoxClass,
  rowButtonClass,
  sectionHeadingClass,
  subTextClass,
  textLinkClass,
} from "./careStyles";

const ORDER_GROUPS: { key: "medications" | "labs" | "imaging" | "referrals"; label: string; to?: string }[] = [
  { key: "medications", label: "Medications started", to: "/patient/medications" },
  { key: "labs", label: "Labs ordered (results in Diagnostics)", to: "/patient/diagnostics" },
  { key: "imaging", label: "Imaging ordered", to: "/patient/diagnostics" },
  // No referrals section exists yet, so these are plain chips.
  { key: "referrals", label: "Referrals" },
];

/** Notes this long or shorter are shown in full inside their part. */
export const INLINE_NOTE_LINES = 15;
/** A longer note shows this many lines, then "Read full note". */
const PREVIEW_LINES = 5;

const byId = <T extends { id: string }>(items: T[]) => [...new Map(items.map((i) => [i.id, i])).values()];

// function(VisitDetailView)
/**
 * The visit-day page.
 *
 * @param day - The visit day (or a single record).
 * @param notes - All notes; the day's own are picked here.
 * @param around - Records from the same department within 14 days.
 * @param previous / next - The neighbouring visit days in the filtered timeline.
 * @param actingAsProfileId - The dependent being viewed, for the original file.
 */
export function VisitDetailView({
  day, notes, around = [], plans = [], previous = null, next = null, actingAsProfileId, onBack, onOpenDay, onOpenNote, onOpenRecord,
}: {
  day: VisitDay;
  notes: Note[];
  around?: Encounter[];
  /** Care plans made at one of this day's visits. */
  plans?: CarePlanOut[];
  previous?: VisitDay | null;
  next?: VisitDay | null;
  actingAsProfileId?: string;
  onBack: () => void;
  onOpenDay?: (day: VisitDay) => void;
  onOpenNote: (note: Note) => void;
  onOpenRecord?: (record: Encounter) => void;
}) {
  const [original, setOriginal] = useState<Source | null>(null);
  const partsRef = useRef<HTMLDivElement>(null);
  const first = day.parts[0];
  const isRecord = first.group === "non_visit";
  const meta = kindMeta(visitKind(first));
  const multiDay = day.parts.length === 1 && first.end && first.end.slice(0, 10) !== first.start.slice(0, 10);
  const longDate = multiDay ? formatWhen(first.start, first.end, false) : formatLongDate(first.start);
  const title = isRecord ? recordTypeLabel(first) : longDate;
  const place = [first.facility, visitDayPlace(day) !== first.facility ? visitDayPlace(day) : ""].filter(Boolean).join(" · ");
  const subline = isRecord
    ? [longDate, formatTime(first.hasTime ? first.start : undefined), place, dayClinicians(day).join(", ")].filter(Boolean).join(" · ")
    : [place, dayClinicians(day).join(", ")].filter(Boolean).join(" · ");
  const lines = summaryLines(day);
  const nextAppointment = day.parts.map((p) => p.nextAppointment).find(Boolean);
  const nextText = nextAppointment ? formatWhen(nextAppointment, undefined, nextAppointment.length > 10) : "";
  const allNotes = day.parts.flatMap((p) => { const n = partNotes(p, notes); return [...n.clinical, ...n.orders]; });
  const inferred = allNotes.some((n) => n.linkConfidence !== "high");
  const diagnoses = byId(day.parts.flatMap((p) => p.diagnoses ?? []));
  const vitals = byId(day.parts.flatMap((p) => p.vitals ?? [])) as Vital[];
  const orders = ORDER_GROUPS
    .map((g) => ({ ...g, items: byId(day.parts.flatMap((p) => p.orders?.[g.key] ?? [])) }))
    .filter((g) => g.items.length > 0);
  const sources = mergeSources(day.parts.flatMap((p) => p.sources));
  /** Nothing to show but the date, place and clinician (as the list says): a message instead of empty cards. */
  const bare = !dayHasDetails(day, dayNotes(day, notes)) && plans.length === 0;
  const hasAside = vitals.length > 0 || (!isRecord && around.length > 0);
  const dateLabel = (d: VisitDay) => formatDateOnly(d.date);

  return (
    <div className={pageClass}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <PageBackButton onClick={onBack} ariaLabel={isRecord ? "Back to Care & Treatment" : "Back to visits"} />
        {!isRecord && (previous || next) && onOpenDay && (
          <nav aria-label="Other visits" className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={!previous}
              aria-label={previous ? `Previous visit, ${dateLabel(previous)}` : "No earlier visit"}
              onClick={() => previous && onOpenDay(previous)}
              className={outlineButtonClass}
            >
              <Icon icon="lucide:arrow-left" className="h-4 w-4" />
              {previous ? dateLabel(previous) : "Previous"}
            </Button>
            <Button
              variant="outline"
              disabled={!next}
              aria-label={next ? `Next visit, ${dateLabel(next)}` : "No later visit"}
              onClick={() => next && onOpenDay(next)}
              className={outlineButtonClass}
            >
              {next ? dateLabel(next) : "Next"}
              <Icon icon="lucide:arrow-right" className="h-4 w-4" />
            </Button>
          </nav>
        )}
      </div>

      {/* Header */}
      <section aria-labelledby="visit-title" className={cn(cardClass, "mb-4")}>
        <div className="flex flex-wrap items-start gap-4">
          <div className={cn(redIconBoxClass, "h-14 w-14")}>
            <Icon icon={meta.icon} className="h-7 w-7 !text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 id="visit-title" className="text-xl font-extrabold text-[#0A2947]">{title}</h1>
            {subline && <p className={cn("!mb-0 mt-1", subTextClass)}>{subline}</p>}
            {!isRecord && (
              <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Parts of this visit">
                {day.parts.map((part) => (
                  <span key={part.id} className={navyChipClass}>
                    {[recordTypeLabel(part), formatTime(part.hasTime ? part.start : undefined)].filter(Boolean).join(" · ")}
                  </span>
                ))}
              </div>
            )}
            <WhereFrom sources={sources} onViewOriginal={setOriginal} className="mt-2" />
          </div>
        </div>

        {lines.length > 0 && (
          <div className={cn("mt-5", factBoxClass)}>
            <p className="!mb-2 text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">VISIT SUMMARY · AS WRITTEN IN YOUR RECORD</p>
            {lines.map((line) => (
              <p key={line.label} className={cn("!mb-1 last:!mb-0", bodyTextClass)}>
                <span className="font-semibold text-[#0A2947]">{line.label}: </span>
                <span>{line.text}</span>
              </p>
            ))}
          </div>
        )}

        {nextText && (
          <dl className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Fact icon="lucide:calendar-clock" label="Next appointment" value={nextText} />
          </dl>
        )}

        {inferred && (
          <p className={cn("mt-4", inferredNoticeClass)}>
            Some notes were linked to this visit by date and clinician, not by the source. Open a note to check it.
          </p>
        )}
      </section>

      {/* Opens above the parts, so it scrolls itself into view. */}
      {original && (
        <OriginalViewer
          source={original}
          actingAsProfileId={actingAsProfileId}
          onClose={() => setOriginal(null)}
          scrollOnOpen
          className="mb-4"
        />
      )}

      {/* Phone: the side column stacks under the parts. */}
      {/* The side column only when it has something (vitals, nearby records). */}
      <div className={cn("grid gap-4", hasAside && "lg:grid-cols-[minmax(0,1fr)_320px]")}>
        <div ref={partsRef} className="min-w-0 space-y-4">
          {/* Reached by Previous / Next, search or a link: say why the page is bare. */}
          {bare && (
            <section aria-label="Nothing more recorded" className={cn(cardClass, "flex items-start gap-3")}>
              <Icon icon="lucide:info" className="mt-0.5 h-5 w-5 shrink-0 text-[#6B7280]" aria-hidden="true" />
              <p className={cn("!mb-0", bodyTextClass)}>{DETAILS_ONLY_TEXT}</p>
            </section>
          )}
          {/* A bare visit: the message only, no empty part cards (the header already says what and where). */}
          {!bare && day.parts.map((part) => (
            <PartSection
              key={part.id}
              part={part}
              notes={notes}
              showHeading={!isRecord}
              onOpenNote={onOpenNote}
            />
          ))}

          <VisitDiagnoses items={diagnoses as LinkedItem[]} />

          {plans.length > 0 && (
            <section aria-labelledby="visit-plans" className={cardClass}>
              <h2 id="visit-plans" className={cn(sectionHeadingClass, "mb-3")}>Care plans from this visit</h2>
              <ul className="!mb-0 grid gap-3 !pl-0">
                {plans.map((plan) => (
                  <li key={plan.id} className="list-none rounded-2xl border border-[#E5E7EB] p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-extrabold text-[#0A2947]">{plan.title}</span>
                      {plan.status && <span className={greyChipClass}>{plan.status}</span>}
                    </div>
                    {[...plan.goals.map((g) => ["Goal", g]), ...plan.activities.map((a) => ["Activity", a])].map(([label, text], i) => (
                      <p key={i} className={cn("!mb-0 mt-1", bodyTextClass)}>
                        <span className="font-semibold">{label}: </span>{text}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {orders.length > 0 && (
            <section aria-labelledby="visit-ordered" className={cardClass}>
              <h2 id="visit-ordered" className={cn(sectionHeadingClass, "mb-3")}>Ordered and started</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {orders.map((group) => (
                  <div key={group.key}>
                    <p className={cn("mb-2", labelTextClass)}>{group.label}</p>
                    <LinkedChips items={group.items} to={group.to} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {hasAside && <aside className="min-w-0 space-y-4 print:hidden">
          {vitals.length > 0 && (
            <section aria-labelledby="visit-vitals" className={cardClass}>
              <h2 id="visit-vitals" className={cn(sectionHeadingClass, "mb-2")}>Vitals</h2>
              <dl>
                {vitals.map((vital) => (
                  <div key={vital.id} className={cn("flex justify-between gap-3 py-2 text-sm first:border-t-0", dividerClass)}>
                    <dt className="text-[#6B7280]">{vital.label}</dt>
                    <dd className="font-bold text-[#0A2947]">{vital.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {!isRecord && around.length > 0 && (
            <section aria-labelledby="visit-around" className={cardClass}>
              <h2 id="visit-around" className={cn(sectionHeadingClass, "!mb-0 !text-base")}>Around this visit</h2>
              <p className={cn("!mb-3", faintTextClass)}>Same department, within 14 days</p>
              <div className="grid gap-2">
                {around.map((record) => {
                  const text = recordText(record, notes.filter((n) => n.encounterId === record.id)).split(/\r?\n/).find((l) => l.trim());
                  return (
                    <Button
                      key={record.id}
                      variant="outline"
                      onClick={() => onOpenRecord?.(record)}
                      className={cn(rowButtonClass, "!w-full !min-w-0 !whitespace-normal")}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-extrabold text-[#0A2947]">
                          {[recordTypeLabel(record), formatDateOnly(record.start)].filter(Boolean).join(" · ")}
                        </span>
                        <span className="block truncate text-xs text-[#6B7280]">{text || record.department || record.facility}</span>
                      </span>
                    </Button>
                  );
                })}
              </div>
            </section>
          )}

        </aside>}
      </div>
    </div>
  );
}

// function(PartSection)
/** One part of the day: its notes, results & orders and record text. Other documents of the file aren't listed here. */
function PartSection({ part, notes, showHeading, onOpenNote }: {
  part: Encounter;
  notes: Note[];
  showHeading: boolean;
  onOpenNote: (note: Note) => void;
}) {
  const { clinical, orders } = partNotes(part, notes);
  const label = recordTypeLabel(part);
  const meta = [formatTime(part.hasTime ? part.start : undefined), part.practitioner || part.department].filter(Boolean).join(" · ");
  const ownText = reflowText(part.recordText?.text?.trim() ?? "");
  const headingId = `part-${part.id}`;

  return (
    <section aria-labelledby={showHeading ? headingId : undefined} aria-label={showHeading ? undefined : label} className={cardClass}>
      {showHeading && (
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 id={headingId} className={cn(sectionHeadingClass, "!mb-0")}>{label}</h2>
          {meta && <span className={subTextClass}>{meta}</span>}
        </div>
      )}

      {!ownText && clinical.length === 0 && orders.length === 0 && (
        <EmptyCardNote>No notes or results were written for this part of the visit.</EmptyCardNote>
      )}

      {ownText && (
        <div className="mb-3">
          <p className={cn("!mb-0 whitespace-pre-wrap break-words leading-relaxed", bodyTextClass)}>{ownText}</p>
        </div>
      )}

      {clinical.length > 0 && (
        <div>
          <GroupHeading id={`notes-${part.id}`} icon="lucide:notebook-pen" title="Doctors’ notes" count={clinical.length}
            hint="What your care team wrote about this visit" />
          <div className="ml-4 grid gap-3 sm:ml-12" role="list" aria-label={`Notes from ${label}`}>
            {clinical.map((note) => <InlineNote key={note.id} note={note} onOpen={() => onOpenNote(note)} />)}
          </div>
        </div>
      )}

      {orders.length > 0 && (
        <OrdersGroup id={`orders-${part.id}`} notes={orders} onOpenNote={onOpenNote} divided={clinical.length > 0 || Boolean(ownText)} />
      )}
    </section>
  );
}

// function(GroupHeading)
/** A group's heading inside a visit part: an icon tile, the title with its count, and one line saying what it holds. */
function GroupHeading({ id, icon, title, count, hint }: { id: string; icon: string; title: string; count: number; hint: string }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EEF2F7]" aria-hidden="true">
        <Icon icon={icon} className="h-[18px] w-[18px] text-[#0A2947]" />
      </span>
      <span className="min-w-0">
        <h3 id={id} className="!mb-0 text-[15px] font-extrabold leading-tight text-[#0A2947]">
          {title} <span className="font-bold text-[#6B7280]">({count})</span>
        </h3>
        <span className={faintTextClass}>{hint}</span>
      </span>
    </div>
  );
}

// function(InlineNote)
/**
 * A note inside its part: 15 lines or fewer in full with its signature line
 * and "Read full note"; longer, its first 5 lines and "Read full note" (the same
 * full-note view, so the same name).
 */
function InlineNote({ note, onOpen }: { note: Note; onOpen: () => void }) {
  const lines = noteLines(note);
  const short = noteLineCount(note) <= INLINE_NOTE_LINES;
  const shown = short ? lines : lines.slice(0, PREVIEW_LINES);
  const when = formatNoteWhen(note.date);

  return (
    <article role="listitem" aria-label={noteTypeLabel(note)} className="rounded-2xl border border-[#E5E7EB] p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-extrabold text-[#0A2947]">{noteTypeLabel(note)}</span>
        {(note.author || when) && <span className={faintTextClass}>{[note.author, when].filter(Boolean).join(" · ")}</span>}
      </div>
      {shown.length > 0 && (
        <p data-testid="inline-note-text" className={cn("!mb-0 mt-2 whitespace-pre-wrap break-words leading-relaxed", bodyTextClass)}>
          {shown.join("\n")}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {short && note.signatureLine ? <span className={faintTextClass}>{note.signatureLine}</span> : <span />}
        <button type="button" onClick={onOpen} className={cn(textLinkClass, "!text-sm")}>Read full note</button>
      </div>
    </article>
  );
}

// function(OrdersGroup)
/** A part's results & orders, under their own heading: the first few, then "Show all N". */
const ORDERS_SHOWN = 3;

function OrdersGroup({ id, notes, onOpenNote, divided }: {
  id: string;
  notes: Note[];
  onOpenNote: (note: Note) => void;
  /** Notes (or the record's text) above: a divider between the two groups. */
  divided: boolean;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? notes : notes.slice(0, ORDERS_SHOWN);
  return (
    <div className={cn(divided && cn("mt-5 pt-5", dividerClass))}>
      <GroupHeading id={`${id}-heading`} icon="lucide:flask-conical" title="Results & orders" count={notes.length}
        hint="Tests ordered or done at this visit, with their results" />
      <div id={id} role="list" aria-labelledby={`${id}-heading`} className="ml-4 grid gap-2 sm:ml-12">
        {shown.map((note) => (
          <div key={note.id} role="listitem"><NoteRow note={note} onOpen={() => onOpenNote(note)} /></div>
        ))}
      </div>
      {notes.length > ORDERS_SHOWN && (
        <button type="button" aria-controls={id} onClick={() => setAll(!all)} className={cn("ml-4 mt-2 sm:ml-12", textLinkClass, "!text-sm")}>
          {all ? "Show fewer" : `Show all ${notes.length}`}
        </button>
      )}
    </div>
  );
}

/** Section chips shown on a note row before "+N more". */
const SECTIONS_SHOWN = 3;

// function(NoteRow)
/** One note: its title (a result's name) and when; opens the note viewer. */
function NoteRow({ note, onOpen }: { note: Note; onOpen: () => void }) {
  const when = formatNoteWhen(note.date);
  // An order's own name is its first section's title ("TSH").
  const name = note.sections[0]?.title?.trim() || noteTypeLabel(note);
  const meta = [note.author, when].filter(Boolean).join(" · ");
  const shown = note.sections.slice(1, 1 + SECTIONS_SHOWN);

  // !w-full / !min-w-0 / !whitespace-normal: an antd Button otherwise sizes to its
  // text and a long line spills out of the card over the column beside it.
  return (
    <Button variant="outline" onClick={onOpen} className={cn(rowButtonClass, "group !w-full !min-w-0 !gap-4 !whitespace-normal")}>
      <span className="flex min-w-0 flex-1 items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EEF2F7]">
          <Icon icon="lucide:file-text" className="h-[18px] w-[18px] text-[#0A2947]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-extrabold text-[#0A2947]">{name}</span>
          {meta && <span className="block truncate text-xs text-[#6B7280]">{meta}</span>}
          {shown.length > 0 && (
            <span className="mt-2 flex flex-wrap gap-1.5" aria-label="Sections">
              {shown.map((s) => (
                <span key={s.key} className={cn(greyChipClass, "max-w-[220px] truncate !font-medium")}>{s.title}</span>
              ))}
            </span>
          )}
        </span>
      </span>
      <Icon icon="lucide:chevron-right" className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Button>
  );
}

// function(Fact)
/** One key fact, styled like the provider page's "Last Seen" box. */
function Fact({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className={factBoxClass}>
      <dt className="flex items-center gap-2 text-sm text-[#9CA3AF]">
        <Icon icon={icon} className="h-4 w-4 text-[#9CA3AF]" />
        {label}
      </dt>
      <dd className="mt-1 font-bold text-[#0A2947]">{value}</dd>
    </div>
  );
}

// function(mergeSources)
/** Each file once, in first-seen order, covering every part's pages. */
export function mergeSources(sources: Source[]): Source[] {
  const merged = new Map<string, Source>();
  for (const s of sources) {
    const key = `${s.kind}|${s.originalRef}|${s.label}`;
    const seen = merged.get(key);
    if (!seen) {
      merged.set(key, { ...s });
      continue;
    }
    if (s.pageStart && (!seen.pageStart || s.pageStart < seen.pageStart)) seen.pageStart = s.pageStart;
    const end = s.pageEnd ?? s.pageStart;
    if (end && (!seen.pageEnd || end > seen.pageEnd)) seen.pageEnd = end;
  }
  return [...merged.values()];
}
